<template>
  <AIVDialog :visible.sync="dialogVisible" title="Mark Form as Satisfied (In-Person)" width="550px">
    <div class="satisfy-form">
      <p class="satisfy-form__subtitle">
        Mark <strong>{{ formName }}</strong> as satisfied for this client.
      </p>

      <div class="satisfy-form__field">
        <label class="field-label">Signer Name</label>
        <el-input v-model="signerName" placeholder="Name of person who signed in-person" />
      </div>

      <div class="satisfy-form__field">
        <label class="field-label">How was this form satisfied?</label>
        <AIVSelect v-model="satisfyMode" :options="satisfyModeOptions" />
      </div>

      <template v-if="satisfyMode === 'upload'">
        <div class="satisfy-form__field">
          <label class="field-label">File</label>
          <input type="file" ref="fileInput" @change="onFileChange" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" />
        </div>
        <div class="satisfy-form__field">
          <label class="field-label">Expiration Date (optional)</label>
          <el-date-picker v-model="expirationDate" type="date" placeholder="Select date" format="MM/dd/yyyy" value-format="yyyy-MM-dd" style="width: 100%;" />
        </div>
      </template>

      <template v-if="satisfyMode === 'link'">
        <div class="satisfy-form__field">
          <label class="field-label">Select Existing Document</label>
          <AIVSelect
            v-model="selectedDocumentId"
            :options="existingDocOptions"
            placeholder="Select a document..."
          />
          <span v-if="existingDocuments.length === 0" style="font-size: 12px; color: #999;">
            No existing documents available for this client.
          </span>
        </div>
      </template>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="submit" :loading="submitting">
          <i class="fa fa-check"></i> Mark as Satisfied
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { clientComplianceService } from "@/serviceHandlers/clientComplianceService";

export default {
  name: "SatisfyComplianceFormModal",
  props: {
    visible: { type: Boolean, default: false },
    clientId: { type: [Number, String], default: null },
    formId: { type: [Number, String], default: null },
    formName: { type: String, default: "" },
    existingDocuments: { type: Array, default: () => [] },
  },
  data() {
    return {
      signerName: "",
      satisfyMode: "upload",
      selectedFile: null,
      expirationDate: null,
      selectedDocumentId: null,
      submitting: false,
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
    existingDocOptions() {
      return this.existingDocuments
        .filter((d) => d.status === "Active")
        .map((d) => ({
          id: d.id,
          name: `${d.document_type}${d.document_name ? ' - ' + d.document_name : ''} (#${d.id})`,
        }));
    },
    satisfyModeOptions() {
      return [
        { id: "upload", name: "Upload New Document" },
        { id: "link", name: "Link Existing Document" },
        { id: "none", name: "No Document (Mark Only)" },
      ];
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.signerName = "";
        this.satisfyMode = "upload";
        this.selectedFile = null;
        this.expirationDate = null;
        this.selectedDocumentId = null;
      }
    },
  },
  methods: {
    onFileChange(e) {
      this.selectedFile = e.target.files[0] || null;
    },
    async submit() {
      if (this.satisfyMode === "upload" && !this.selectedFile) {
        this.$notifyError("Please select a file to upload.");
        return;
      }
      if (this.satisfyMode === "link" && !this.selectedDocumentId) {
        this.$notifyError("Please select an existing document.");
        return;
      }

      this.submitting = true;
      try {
        if (this.satisfyMode === "upload") {
          const formData = new FormData();
          formData.append("file", this.selectedFile);
          formData.append("complianceFormId", this.formId);
          formData.append("signerName", this.signerName || "In-Person");
          if (this.expirationDate) {
            formData.append("expiration_date", this.expirationDate);
          }
          await clientComplianceService.satisfyForm(this.clientId, formData);
        } else {
          const payload = {
            complianceFormId: this.formId,
            signerName: this.signerName || "In-Person",
          };
          if (this.satisfyMode === "link") {
            payload.documentId = this.selectedDocumentId;
          }
          await clientComplianceService.satisfyForm(this.clientId, payload);
        }

        this.$notifySuccess(`${this.formName} marked as satisfied.`);
        this.$emit("satisfied");
        this.dialogVisible = false;
      } catch (err) {
        this.$notifyError("Failed to mark form as satisfied: " + (err.response?.data?.message || err.message || err));
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.satisfy-form__subtitle {
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.satisfy-form__field {
  margin-bottom: 1rem;
}

.field-label {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px 0;
  display: block;
}

input[type="file"] {
  color: #e0e0e0;
  font-size: 13px;
}
</style>
